/**
 * SearchResultsTable — table-view of Apollo/LinkedIn search results.
 *
 * Visually mirrors features/leads/leads-table.tsx so the discovery and
 * pipeline tables read the same. Columns collapse on smaller breakpoints:
 *   - <md  : name + company only (title moves under the name)
 *   - md+  : adds job title column
 *   - lg+  : adds location, linkedin profile link
 */

import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  Sparkles,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { LinkedInProfile } from './use-search'

function getInitials(name: string): string {
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

type SearchResultsTableProps = {
  readonly results: readonly LinkedInProfile[]
  readonly selectedIds: ReadonlySet<string>
  readonly enrichingIds: ReadonlySet<string>
  readonly onSelect: (id: string, selected: boolean) => void
  readonly onSelectAll: (ids: readonly string[], selected: boolean) => void
  readonly onImport: (profile: LinkedInProfile) => void
  readonly onEnrich: (profile: LinkedInProfile) => void
}

export function SearchResultsTable({
  results,
  selectedIds,
  enrichingIds,
  onSelect,
  onSelectAll,
  onImport,
  onEnrich,
}: SearchResultsTableProps) {
  const importableResults = results.filter((r) => !r.in_pipeline)
  const allImportableSelected =
    importableResults.length > 0 &&
    importableResults.every((r) => selectedIds.has(r.id))
  const someImportableSelected = importableResults.some((r) =>
    selectedIds.has(r.id),
  )

  return (
    <div className="rounded-lg border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]">
              {importableResults.length > 0 && (
                <Checkbox
                  checked={
                    allImportableSelected
                      ? true
                      : someImportableSelected
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(v) =>
                    onSelectAll(importableResults.map((r) => r.id), !!v)
                  }
                  aria-label="Select all"
                />
              )}
            </TableHead>
            <TableHead className="min-w-[180px]">Name</TableHead>
            <TableHead className="hidden md:table-cell">Job Title</TableHead>
            <TableHead className="hidden lg:table-cell">Location</TableHead>
            <TableHead className="hidden lg:table-cell">Profile</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((profile) => {
            const isPreview = !profile.is_enriched
            const isEnriching = enrichingIds.has(profile.id)
            return (
              <TableRow
                key={profile.id}
                className={cn(profile.in_pipeline && 'bg-emerald-500/[0.04]')}
              >
                <TableCell>
                  {!profile.in_pipeline && (
                    <Checkbox
                      checked={selectedIds.has(profile.id)}
                      onCheckedChange={(v) => onSelect(profile.id, !!v)}
                      aria-label={`Select ${profile.name}`}
                    />
                  )}
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar className="h-7 w-7 shrink-0">
                      {profile.avatar_url && (
                        <AvatarImage src={profile.avatar_url} alt={profile.name} />
                      )}
                      <AvatarFallback className="text-xs">
                        {getInitials(profile.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium">
                          {profile.name}
                        </span>
                        {profile.in_pipeline && (
                          <CheckCircle2
                            className="h-3.5 w-3.5 shrink-0 text-emerald-300"
                            aria-label="Already in pipeline"
                          />
                        )}
                        {isPreview && !profile.in_pipeline && (
                          <Badge
                            variant="outline"
                            className="shrink-0 px-1.5 text-[10px]"
                          >
                            Preview
                          </Badge>
                        )}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {profile.company || '-'}
                      </p>
                      {/* Title is hidden in its own column on <md, so show
                          it under the name on mobile. */}
                      {profile.title && (
                        <p className="truncate text-xs text-muted-foreground md:hidden">
                          {profile.title}
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>

                <TableCell className="hidden md:table-cell">
                  <span className="text-xs text-muted-foreground">
                    {profile.title || '-'}
                  </span>
                </TableCell>

                <TableCell className="hidden lg:table-cell">
                  <span className="text-xs text-muted-foreground">
                    {profile.location || '-'}
                  </span>
                </TableCell>

                <TableCell className="hidden lg:table-cell">
                  {profile.linkedin_url ? (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs text-[var(--color-cobalt-bright)] hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      LinkedIn
                    </a>
                  ) : (
                    <span className="text-xs text-muted-foreground/40">-</span>
                  )}
                </TableCell>

                <TableCell className="text-right">
                  {profile.in_pipeline ? (
                    <Badge className="gap-1 text-xs bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                      <CheckCircle2 className="h-3 w-3" />
                      Added
                    </Badge>
                  ) : isPreview ? (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => onEnrich(profile)}
                      disabled={isEnriching || !profile.apollo_id}
                      className="gap-1 text-xs"
                    >
                      {isEnriching ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          <span className="hidden sm:inline">Enriching…</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-3 w-3" />
                          <span>Enrich</span>
                          <span className="hidden sm:inline">(1 credit)</span>
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onImport(profile)}
                      className="text-xs"
                    >
                      Import
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
